import { useRef, useCallback, useEffect } from 'react';
import { useTimelineEntryReassignment } from './useTimelineEntryReassignment';

interface ReassignmentMetrics {
  callCount: number;
  lastCallTime: number;
  birthdateHistory: Map<string, string>;
  failureCount: number;
  lastFailureTime: number;
}

interface SmartReassignmentOptions {
  userId?: string;
  birthdate?: string;
  shouldReassign: boolean;
  timelineExists: boolean;
  force?: boolean;
}

export const useSmartReassignment = () => {
  const { reassignEntries, isReassigning } = useTimelineEntryReassignment();
  const metricsRef = useRef<ReassignmentMetrics>({
    callCount: 0,
    lastCallTime: 0,
    birthdateHistory: new Map(),
    failureCount: 0,
    lastFailureTime: 0
  });
  
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const circuitBreakerRef = useRef(false);

  // Performance monitoring
  const logMetrics = useCallback(() => {
    const metrics = metricsRef.current;
    console.log('📊 Reassignment Metrics:', {
      totalCalls: metrics.callCount,
      recentFailures: metrics.failureCount,
      circuitBreakerActive: circuitBreakerRef.current,
      cachedBirthdates: metrics.birthdateHistory.size,
      lastCallMinutesAgo: Math.round((Date.now() - metrics.lastCallTime) / 60000)
    });
  }, []);

  // Circuit breaker: prevent calls if too many failures
  const checkCircuitBreaker = useCallback(() => {
    const metrics = metricsRef.current;
    const now = Date.now();
    
    // Reset circuit breaker after 5 minutes
    if (circuitBreakerRef.current && now - metrics.lastFailureTime > 5 * 60 * 1000) {
      circuitBreakerRef.current = false;
      metrics.failureCount = 0;
      console.log('🔄 Circuit breaker reset');
    }
    
    // Trip circuit breaker if too many failures
    if (metrics.failureCount >= 3) {
      circuitBreakerRef.current = true;
      console.warn('🚨 Circuit breaker activated - too many reassignment failures');
      return false;
    }
    
    return !circuitBreakerRef.current;
  }, []);

  // Rate limiting: prevent calls more than once per minute
  const checkRateLimit = useCallback(() => {
    const metrics = metricsRef.current;
    const now = Date.now();
    const timeSinceLastCall = now - metrics.lastCallTime;
    
    if (timeSinceLastCall < 60000) { // 1 minute
      console.log('🚦 Rate limit: Reassignment throttled');
      return false;
    }
    
    return true;
  }, []);

  // Smart birthdate change detection
  const hasBirthdateChanged = useCallback((userId: string, newBirthdate: string) => {
    const metrics = metricsRef.current;
    const lastBirthdate = metrics.birthdateHistory.get(userId);
    
    if (!lastBirthdate) {
      // First time seeing this user - cache the birthdate but don't reassign
      metrics.birthdateHistory.set(userId, newBirthdate);
      console.log('💾 Cached initial birthdate for user:', userId);
      return false;
    }
    
    if (lastBirthdate !== newBirthdate) {
      console.log('🔄 Birthdate changed detected:', { userId, from: lastBirthdate, to: newBirthdate });
      metrics.birthdateHistory.set(userId, newBirthdate);
      return true;
    }
    
    return false;
  }, []);

  // Debounced smart reassignment
  const smartReassign = useCallback((options: SmartReassignmentOptions) => {
    const { userId, birthdate, shouldReassign, timelineExists, force = false } = options;
    
    if (!userId || !birthdate || !shouldReassign || !timelineExists) {
      return;
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the reassignment call
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Safety checks
        if (!checkCircuitBreaker() || (!force && !checkRateLimit())) {
          return;
        }

        // Only reassign if birthdate actually changed (unless forced)
        if (!force && !hasBirthdateChanged(userId, birthdate)) {
          return;
        }

        const metrics = metricsRef.current;
        metrics.callCount++;
        metrics.lastCallTime = Date.now();

        console.log('🔄 Smart reassignment triggered:', { userId, birthdate, force });
        
        const result = await reassignEntries(userId, birthdate);
        
        if (result.errors.length > 0) {
          metrics.failureCount++;
          metrics.lastFailureTime = Date.now();
          console.error('❌ Reassignment failed:', result.errors);
        } else {
          // Reset failure count on success
          metrics.failureCount = 0;
          console.log('✅ Reassignment completed:', { 
            movedCount: result.movedCount,
            userId 
          });
        }
        
      } catch (error) {
        const metrics = metricsRef.current;
        metrics.failureCount++;
        metrics.lastFailureTime = Date.now();
        console.error('❌ Smart reassignment error:', error);
      }
    }, 1000); // 1 second debounce
    
  }, [reassignEntries, checkCircuitBreaker, checkRateLimit, hasBirthdateChanged]);

  // Manual force reassignment (for user-initiated actions)
  const forceReassign = useCallback(async (userId: string, birthdate: string) => {
    if (isReassigning) {
      console.log('⏳ Reassignment already in progress');
      return;
    }

    console.log('🔧 Manual reassignment requested:', { userId, birthdate });
    smartReassign({ 
      userId, 
      birthdate, 
      shouldReassign: true, 
      timelineExists: true, 
      force: true 
    });
  }, [smartReassign, isReassigning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Performance monitoring interval
  useEffect(() => {
    const interval = setInterval(logMetrics, 5 * 60 * 1000); // Every 5 minutes
    return () => clearInterval(interval);
  }, [logMetrics]);

  return {
    smartReassign,
    forceReassign,
    isReassigning,
    getMetrics: () => ({ ...metricsRef.current }),
    resetCircuitBreaker: () => {
      circuitBreakerRef.current = false;
      metricsRef.current.failureCount = 0;
      console.log('🔄 Circuit breaker manually reset');
    }
  };
};